<template>
  <div class="compare-box">
    <div class="chart-wrap">
    <div class="table-title">同款多平台比价</div>
    <table class="price-table">
      <thead>
        <tr>
          <th>序号</th>
          <th>商品名称</th>
          <th>产业乡镇</th>
          <th>规格</th>
          <th>淘宝价</th>
          <th>本地商城</th>
          <th>最低价平台</th>
          <th>跨平台差价</th>
          <th>价格波动指数</th>
          <th>状态标签</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.id">
          <td>{{ row.id }}</td>
          <td>{{ row.name }}</td>
          <td>{{ row.town }}</td>
          <td>{{ row.spec }}</td>
          <td>{{ row.taobao }}</td>
          <td>{{ row.local }}</td>
          <td>{{ row.lowest }}</td>
          <td>{{ row.diff }}</td>
          <td :class="row.trend > 0 ? 'up' : 'down'">{{ row.trend > 0 ? "+" : "" }}{{ row.trend }}%</td>
          <td :class="row.statusClass">{{ row.status }}</td>
        </tr>
      </tbody>
    </table>
    </div>
    <div class="chart-wrap1">
    <div class="bar-title">单品多平台价格对比</div>
    <ChartBox :option="option" />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ChartBox from "@/components/xiaofeiTradeData/ChartBox.vue";
import { formatPrice, lowestPlatform, platforms, priceProducts, priceSpread } from "./priceData";

const rows = priceProducts.slice(0, 5).map((product, index) => {
  const diff = priceSpread(product);
  const minPrice = Math.min(...platforms.map((platform) => product.prices[platform.key]));
  const trend = (diff / minPrice) * 100;
  return {
    id: index + 1,
    name: product.name,
    town: product.town,
    spec: product.spec,
    taobao: formatPrice(product.prices.taobao),
    local: formatPrice(product.prices.local),
    lowest: lowestPlatform(product).name,
    diff: formatPrice(diff),
    trend: Number(trend.toFixed(1)),
    status: trend >= 20 ? "重点监测" : trend >= 12 ? "小幅波动" : "价格正常",
    statusClass: trend >= 20 ? "danger" : trend >= 12 ? "warn" : "ok",
  };
});

const compareProducts = [priceProducts[0], priceProducts[1]];
const comparePlatforms = [
  platforms[0],
  platforms[1],
  platforms[2],
  platforms[4],
  platforms[3],
];
const platformNames = comparePlatforms.map((platform) => platform.name);

function barGradient(startColor, endColor) {
  return {
    type: "linear",
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: startColor },
      { offset: 1, color: endColor },
    ],
  };
}

const primaryBarColors = [
  barGradient("#2bb8ff", "#0b54d5"),
  barGradient("#27c8ff", "#087ed5"),
  barGradient("#3d9cff", "#245ee4"),
  barGradient("#31b7ff", "#1d74d8"),
  barGradient("#7b66ff", "#5946d0"),
];

const compareBarColors = [
  barGradient("#1fd8f0", "#1482cf"),
  barGradient("#22d4f2", "#1392d9"),
  barGradient("#8e70ff", "#614ad8"),
  barGradient("#d58d70", "#935978"),
  barGradient("#8b75ff", "#5947d0"),
];

const priceLabel = {
  show: true,
  position: "top",
  distance: 6,
  color: "#edf8ff",
  fontSize: 22,
  fontWeight: "bold",
  formatter: ({ value }) => Number(value).toFixed(2),
};

const option = computed(() => ({
  color: ["#2c75ff", "#1cc7ff"],
  grid: { top: 88, left: 70, right: 38, bottom: 58 },
  legend: {
    top: 16,
    right: 46,
    icon: "roundRect",
    itemWidth: 22,
    itemHeight: 10,
    itemGap: 26,
    textStyle: { color: "#cfefff", fontSize: 18 },
    data: compareProducts.map((product) => product.name),
  },
  tooltip: {
    trigger: "axis",
    textStyle: { fontSize: 24 },
    axisPointer: { type: "shadow" },
    formatter: (params) => {
      const title = params?.[0]?.axisValueLabel || "";
      const lines = params.map((item) => `${item.marker}${item.seriesName}：${Number(item.value).toFixed(2)}元`);
      return [title, ...lines].join("<br/>");
    },
  },
  xAxis: {
    type: "category",
    data: platformNames,
    axisTick: { show: false },
    axisLine: { lineStyle: { color: "rgba(95, 164, 230, .42)" } },
    axisLabel: { color: "#d9f2ff", fontSize: 22, fontWeight: "bold", margin: 18 },
  },
  yAxis: {
    type: "value",
    min: 0,
    max: 50,
    axisLabel: { color: "#cde8ff", fontSize: 18 },
    splitLine: { lineStyle: { color: "rgba(118, 178, 230, .18)" } },
  },
  series: [
    {
      name: compareProducts[0].name,
      type: "bar",
      barWidth: 46,
      barGap: "100%",
      label: priceLabel,
      itemStyle: {
        borderRadius: [2, 2, 0, 0],
        color: ({ dataIndex }) => primaryBarColors[dataIndex],
      },
      emphasis: { focus: "series" },
      data: comparePlatforms.map((platform) => compareProducts[0].prices[platform.key]),
    },
    {
      name: compareProducts[1].name,
      type: "bar",
      barWidth: 46,
      label: priceLabel,
      itemStyle: {
        borderRadius: [2, 2, 0, 0],
        color: ({ dataIndex }) => compareBarColors[dataIndex],
      },
      emphasis: { focus: "series" },
      data: comparePlatforms.map((platform) => compareProducts[1].prices[platform.key]),
    },
  ],
}));
</script>

<style lang="less" scoped>
.compare-box {
  height: 100%;
  padding: 22px 28px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.chart-wrap{
  height: 700px;
  display: flex;
  flex-direction: column;
}
.chart-wrap1{
  flex: 1;
}
.table-title,
.bar-title {
  width: 100%;
  height: 75px;
  line-height: 40px;
  color: #dff6ff;
  text-align: center;
  font-size: 30px;
  font-weight: bold;
  background: url(../../assets/img/title.png) no-repeat;
}

.price-table {
  width: 100%;
  flex: 1;
  border-collapse: collapse;
  margin-top: 10px;
  color: #c6e7ff;
  font-size: 26px;
}

.price-table th {
  height: 90px;
  background: rgba(23, 91, 167, 0.55);
  color: #dff7ff;
  font-weight: bold;
}

.price-table td {
  height: 48px;
  text-align: center;
  background: rgba(12, 64, 133, 0.4);
  border-bottom: 1px solid rgba(58, 138, 218, 0.25);
}

.price-table tbody tr:first-child td {
  background: rgba(35, 107, 202, 0.72);
}

.up {
  color: #ffe06a;
  font-weight: bold;
}

.down {
  color: #5decb9;
  font-weight: bold;
}

.danger {
  color: #ff7d91;
}

.warn {
  color: #ffe06a;
}

.ok {
  color: #5decb9;
}

.bar-title {
  margin-top: 44px;
}
</style>
